import { motion } from "framer-motion";
import { Check, TrendingDown, FileSearch, BadgeCheck } from "lucide-react";
import { SERVICES } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const TariffPanel = () => (
  <div className="relative rounded-[1.4rem] overflow-hidden bg-night aspect-[4/3] flex flex-col justify-between p-7 shadow-[0_30px_80px_rgba(18,24,32,0.2)]">
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60">Analisi bolletta</span>
      <FileSearch size={18} className="text-copper" />
    </div>
    <div>
      <div className="flex items-end gap-3 mb-2">
        <span className="font-display font-extrabold text-4xl text-paper/40 line-through decoration-copper">58€</span>
        <TrendingDown size={22} className="text-copper mb-1.5" />
        <span className="font-display font-extrabold text-5xl text-paper">29€</span>
        <span className="font-mono text-xs text-paper/60 mb-1.5">/mese</span>
      </div>
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-paper/60">stesso servizio, operatore giusto</p>
    </div>
    <div className="flex items-center gap-2 text-paper/85">
      <BadgeCheck size={15} className="text-copper" />
      <span className="font-body text-sm">Consulenza indipendente, nessun vincolo</span>
    </div>
  </div>
);

const Chapter = ({ service, index }) => {
  const reversed = index % 2 === 1;
  return (
    <motion.article
      data-testid={`service-chapter-${service.number}`}
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: EASE }}
      className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reversed ? "" : ""}`}
    >
      <div className={reversed ? "lg:order-2" : ""}>
        <div className="flex items-baseline gap-4 mb-4">
          <span className="font-mono text-sm text-copper tracking-widest">{service.number}</span>
          <span className="flex-1 h-px bg-line" aria-hidden="true" />
        </div>
        <h3 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink leading-tight">
          {service.title}
        </h3>
        <p className="mt-5 text-base sm:text-lg leading-relaxed text-smoke">{service.description}</p>
        <ul className="mt-7 grid sm:grid-cols-2 gap-3">
          {service.highlights.map((h) => (
            <li key={h} className="flex items-center gap-2.5 text-sm text-ink">
              <span className="w-5 h-5 rounded-full bg-copper/10 flex items-center justify-center shrink-0">
                <Check size={11} strokeWidth={3} className="text-copper" />
              </span>
              {h}
            </li>
          ))}
        </ul>
      </div>
      <div className={reversed ? "lg:order-1" : ""}>
        {service.customPanel ? (
          <TariffPanel />
        ) : (
          <motion.div
            whileHover={{ scale: 0.985 }}
            transition={{ duration: 0.5, ease: EASE }}
            className={`relative rounded-[1.4rem] overflow-hidden aspect-[4/3] shadow-[0_30px_80px_rgba(18,24,32,0.16)] group ${service.contain ? "bg-[#dde1da]" : "bg-night"}`}
          >
            <img
              src={service.image}
              alt={service.alt}
              loading="lazy"
              className={`w-full h-full transition-transform duration-700 ease-out group-hover:scale-105 ${service.contain ? "object-contain" : "object-cover"}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-night/40 via-transparent to-transparent" aria-hidden="true" />
            <span className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/90 bg-night/40 backdrop-blur-md px-3 py-1.5 rounded-full">
              Capitolo {service.number}
            </span>
          </motion.div>
        )}
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="servizi" data-testid="services-section" className="relative py-20 sm:py-28 lg:py-36">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="max-w-2xl mb-16 sm:mb-24"
      >
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-copper mb-5 flex items-center gap-3">
          <span className="w-8 h-px bg-copper inline-block" /> Il manifesto dei servizi
        </p>
        <h2 className="font-display font-bold tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-5xl text-ink">
          Sei capitoli, un solo obiettivo: <span className="text-copper">tecnologia che torna a funzionare.</span>
        </h2>
      </motion.div>
      <div className="flex flex-col gap-20 sm:gap-28">
        {SERVICES.map((s, i) => (
          <Chapter key={s.number} service={s} index={i} />
        ))}
      </div>
    </div>
  </section>
);
